import type { DictationAdapter } from "@assistant-ui/react";

type Unsubscribe = () => void;

export interface DeepgramDictationAdapterOptions {
  baseUrl: string;
  getHeaders: () => Record<string, string>;
  model?: string;
  language?: string;
}

/**
 * Custom DictationAdapter that uses Deepgram's live streaming WebSocket API
 * for real-time speech-to-text. Audio stays under our control (HIPAA-friendly).
 */
export class DeepgramDictationAdapter implements DictationAdapter {
  disableInputDuringDictation = false;

  private baseUrl: string;
  private getHeaders: () => Record<string, string>;
  private model: string;
  private language: string;

  constructor(options: DeepgramDictationAdapterOptions) {
    this.baseUrl = options.baseUrl;
    this.getHeaders = options.getHeaders;
    this.model = options.model ?? "nova-3";
    this.language = options.language ?? "en";
  }

  listen(): DictationAdapter.Session {
    return new DeepgramDictationSession(this.baseUrl, this.getHeaders, this.model, this.language);
  }
}

class DeepgramDictationSession implements DictationAdapter.Session {
  status: DictationAdapter.Status = { type: "starting" };

  private speechStartCallbacks: Array<() => void> = [];
  private speechEndCallbacks: Array<(result: DictationAdapter.Result) => void> = [];
  private speechCallbacks: Array<(result: DictationAdapter.Result) => void> = [];

  private ws: WebSocket | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private accumulatedTranscript = "";

  constructor(
    private baseUrl: string,
    private getHeaders: () => Record<string, string>,
    private model: string,
    private language: string,
  ) {
    this.init();
  }

  private async init() {
    try {
      // Step 1: Get Deepgram key from backend
      const tokenRes = await fetch(`${this.baseUrl}/superdoc/dictation/token/`, {
        method: "POST",
        headers: this.getHeaders(),
      });

      if (!tokenRes.ok) {
        console.error("[Dictation] Failed to get token:", tokenRes.status);
        this.status = { type: "ended", reason: "error" };
        return;
      }

      const { key, url } = (await tokenRes.json()) as { key: string; url: string };

      // Step 2: Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Step 3: Open WebSocket to Deepgram
      const wsUrl = `${url}?model=${this.model}&language=${this.language}&smart_format=true&interim_results=true&endpointing=300&utterance_end_ms=1500`;
      this.ws = new WebSocket(wsUrl, ["token", key]);

      this.ws.onopen = () => {
        this.status = { type: "running" };
        this.startRecording();
      };

      this.ws.onmessage = (event) => {
        this.handleDeepgramMessage(event.data as string);
      };

      this.ws.onerror = (event) => {
        console.error("[Dictation] WebSocket error:", event);
        this.cleanup();
        this.status = { type: "ended", reason: "error" };
      };

      this.ws.onclose = () => {
        if (this.status.type !== "ended") {
          this.status = { type: "ended", reason: "stopped" };
        }
      };
    } catch (err) {
      console.error("[Dictation] Init error:", err);
      this.status = { type: "ended", reason: "error" };
    }
  }

  private startRecording() {
    if (!this.stream) return;

    // Use a format that Deepgram supports well
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";

    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType,
      audioBitsPerSecond: 16000,
    });

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0 && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(event.data);
      }
    };

    // Send audio chunks every 250ms
    this.mediaRecorder.start(250);

    // Notify speech started
    for (const cb of this.speechStartCallbacks) cb();
  }

  private handleDeepgramMessage(data: string) {
    try {
      const msg = JSON.parse(data) as {
        type?: string;
        channel?: {
          alternatives?: Array<{ transcript?: string }>;
        };
        is_final?: boolean;
        speech_final?: boolean;
      };

      if (msg.type !== "Results") return;

      const transcript = msg.channel?.alternatives?.[0]?.transcript;
      if (!transcript) return;

      if (msg.is_final) {
        // Final result for this utterance segment — accumulate it
        this.accumulatedTranscript += (this.accumulatedTranscript ? " " : "") + transcript;

        // Notify with accumulated transcript
        const result: DictationAdapter.Result = {
          transcript: this.accumulatedTranscript,
          isFinal: false, // Not final until session ends
        };
        for (const cb of this.speechCallbacks) cb(result);

        if (msg.speech_final) {
          // End of a natural utterance — emit as speech end
          const endResult: DictationAdapter.Result = {
            transcript: this.accumulatedTranscript,
            isFinal: true,
          };
          for (const cb of this.speechEndCallbacks) cb(endResult);
          this.accumulatedTranscript = "";
        }
      } else {
        // Interim result — show accumulated + current interim
        const interimText = this.accumulatedTranscript ? `${this.accumulatedTranscript} ${transcript}` : transcript;
        const result: DictationAdapter.Result = {
          transcript: interimText,
          isFinal: false,
        };
        for (const cb of this.speechCallbacks) cb(result);
      }
    } catch {
      // Ignore parse errors
    }
  }

  private cleanup() {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
    this.mediaRecorder = null;

    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop();
      this.stream = null;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Send close frame to Deepgram
      this.ws.send(JSON.stringify({ type: "CloseStream" }));
      this.ws.close();
    }
    this.ws = null;
  }

  async stop(): Promise<void> {
    // Finalize any remaining transcript
    if (this.accumulatedTranscript) {
      const result: DictationAdapter.Result = {
        transcript: this.accumulatedTranscript,
        isFinal: true,
      };
      for (const cb of this.speechEndCallbacks) cb(result);
      this.accumulatedTranscript = "";
    }

    this.cleanup();
    this.status = { type: "ended", reason: "stopped" };
  }

  cancel(): void {
    this.accumulatedTranscript = "";
    this.cleanup();
    this.status = { type: "ended", reason: "cancelled" };
  }

  onSpeechStart(callback: () => void): Unsubscribe {
    this.speechStartCallbacks.push(callback);
    return () => {
      this.speechStartCallbacks = this.speechStartCallbacks.filter((cb) => cb !== callback);
    };
  }

  onSpeechEnd(callback: (result: DictationAdapter.Result) => void): Unsubscribe {
    this.speechEndCallbacks.push(callback);
    return () => {
      this.speechEndCallbacks = this.speechEndCallbacks.filter((cb) => cb !== callback);
    };
  }

  onSpeech(callback: (result: DictationAdapter.Result) => void): Unsubscribe {
    this.speechCallbacks.push(callback);
    return () => {
      this.speechCallbacks = this.speechCallbacks.filter((cb) => cb !== callback);
    };
  }
}
